import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useDomains } from "@/context/DomainContext";
import { PageHeader, NoDomain, MigaduNotice, EmptyState, timeAgo } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Users as UsersIcon, Plus, Search, MoreVertical, KeyRound, Pause, Play, Archive, ArchiveRestore,
  Pencil, FolderInput, Trash2, ShieldCheck, ShieldAlert, ShieldOff, LogOut, Smartphone, AppWindow,
  RefreshCw, Loader2, Crown, HardDrive, Share2, FileText, Info, Copy, MessageSquare, Layers,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

function formatBytes(b) {
  if (b == null) return "—";
  const n = Number(b);
  if (isNaN(n)) return "—";
  if (n === 0) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(n) / Math.log(1024));
  return `${(n / Math.pow(1024, i)).toFixed(i ? 2 : 0)} ${u[i]}`;
}

export default function Users() {
  const { selected } = useDomains();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ primary_email: "", given_name: "", family_name: "", password: "" });
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameForm, setRenameForm] = useState({ given_name: "", family_name: "" });
  const [moveTarget, setMoveTarget] = useState(null);
  const [ou, setOu] = useState("/");
  const [secTarget, setSecTarget] = useState(null);
  const [view, setView] = useState("active");
  const [deleted, setDeleted] = useState([]);
  const [loadingDeleted, setLoadingDeleted] = useState(false);
  const [detailsTarget, setDetailsTarget] = useState(null);
  const [detailsData, setDetailsData] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [createdCreds, setCreatedCreds] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkTab, setBulkTab] = useState("create");
  const [bulkText, setBulkText] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);

  const did = selected?.id;
  const isEmailOnly = selected?.provider === "migadu" || selected?.provider === "hostinger";

  const load = useCallback(async (query) => {
    if (!did || isEmailOnly) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/domains/${did}/users`, { params: query ? { q: query } : {} });
      setUsers(data);
    } catch (e) { toast.error(apiError(e)); setUsers([]); }
    finally { setLoading(false); }
  }, [did, isEmailOnly]);

  useEffect(() => { load(); }, [load]);

  const loadDeleted = useCallback(async () => {
    if (!did || isEmailOnly) return;
    setLoadingDeleted(true);
    try {
      const { data } = await api.get(`/domains/${did}/users-deleted`);
      setDeleted(data);
    } catch (e) { toast.error(apiError(e)); setDeleted([]); }
    finally { setLoadingDeleted(false); }
  }, [did]);

  useEffect(() => { if (view === "deleted") loadDeleted(); }, [view, loadDeleted]);

  const restore = async (u) => {
    try { await api.post(`/domains/${did}/users/${u.id}/undelete`); toast.success("User restored"); loadDeleted(); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const act = async (p, msg) => {
    try { await p; toast.success(msg); load(q); }
    catch (e) { toast.error(apiError(e)); }
  };

  const openDetails = async (u) => {
    setDetailsTarget(u); setDetailsData(null); setDetailsLoading(true);
    try { const { data } = await api.get(`/domains/${did}/users/${u.primaryEmail}/details`); setDetailsData(data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setDetailsLoading(false); }
  };

  if (!selected) return <NoDomain />;
  if (isEmailOnly) return <MigaduNotice />;

  const create = async () => {
    try {
      await api.post(`/domains/${did}/users`, form);
      setCreatedCreds({ email: form.primary_email, name: `${form.given_name} ${form.family_name}`.trim(), password: form.password });
      toast.success("User created");
      setCreateOpen(false); setForm({ primary_email: "", given_name: "", family_name: "", password: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const copyText = (t) => { try { navigator.clipboard.writeText(t); toast.success("Copied — paste into WhatsApp/email"); } catch { toast.error("Copy failed"); } };
  const credMsg = (c) => `Hello ${c.name || "there"} 👋\n\nYour ${selected.domain} account is ready:\n\n📧 Username: ${c.email}\n🔑 Password: ${c.password}\n\nSign in at https://mail.google.com and change your password on first login.`;
  const bulkCreateMsg = (data) => `New accounts on ${selected.domain}:\n\n` +
    data.filter((r) => r.ok).map((r) => `👤 ${r.name}\n📧 ${r.email}\n🔑 ${r.password}`).join("\n\n") +
    `\n\nSign in at https://mail.google.com and change your password on first login.`;

  const runBulk = async () => {
    setBulkBusy(true); setBulkResults(null);
    try {
      if (bulkTab === "create") {
        const users = bulkText.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
          const [given_name, family_name, primary_email] = l.split(",").map((x) => (x || "").trim());
          return { given_name, family_name, primary_email };
        }).filter((u) => u.primary_email && u.given_name);
        if (!users.length) { toast.error("Each line must be: First, Last, Email"); setBulkBusy(false); return; }
        const { data } = await api.post(`/domains/${did}/users/bulk-create`, { users });
        setBulkResults({ type: "create", data });
      } else {
        const emails = bulkText.split(/[\n,]+/).map((x) => x.trim()).filter(Boolean);
        if (!emails.length) { toast.error("Add at least one email"); setBulkBusy(false); return; }
        let url, payload;
        if (bulkTab === "suspend") { url = "bulk-suspend"; payload = { emails, suspended: true }; }
        else if (bulkTab === "unsuspend") { url = "bulk-suspend"; payload = { emails, suspended: false }; }
        else if (bulkTab === "undelete") { url = "bulk-undelete"; payload = { emails }; }
        else { url = "bulk-delete"; payload = { emails }; }
        const { data } = await api.post(`/domains/${did}/users/${url}`, payload);
        setBulkResults({ type: bulkTab, data });
      }
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBulkBusy(false); }
  };

  const activeCount = users.filter((u) => !u.suspended && !u.archived).length;
  const suspendedCount = users.filter((u) => u.suspended).length;
  const adminCount = users.filter((u) => u.isAdmin).length;

  return (
    <div className="animate-fade-up">
      <PageHeader title="User Management" subtitle={`Managing ${selected.domain}`} icon={UsersIcon}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-xl bg-white/60" onClick={() => { setBulkOpen(true); setBulkResults(null); setBulkText(""); }} data-testid="bulk-actions-button"><Layers className="mr-1 h-4 w-4" />Bulk</Button>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="glossy-emerald relative rounded-xl" data-testid="create-user-button">
                <span className="spec-shine" /><Plus className="mr-1 h-4 w-4" />Create User
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle className="font-heading">Create user in {selected.domain}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1.5"><Label>Primary email</Label>
                  <Input value={form.primary_email} onChange={(e) => setForm({ ...form, primary_email: e.target.value })} placeholder={`jane@${selected.domain}`} data-testid="new-user-email" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>First name</Label>
                    <Input value={form.given_name} onChange={(e) => setForm({ ...form, given_name: e.target.value })} data-testid="new-user-given" /></div>
                  <div className="space-y-1.5"><Label>Last name</Label>
                    <Input value={form.family_name} onChange={(e) => setForm({ ...form, family_name: e.target.value })} data-testid="new-user-family" /></div>
                </div>
                <div className="space-y-1.5"><Label>Temporary password</Label>
                  <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 8 chars — user changes at first login" data-testid="new-user-password" /></div>
              </div>
              <DialogFooter><Button onClick={create} className="glossy-emerald relative w-full rounded-xl" data-testid="submit-create-user"><span className="spec-shine" />Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      <div className="glass rounded-3xl p-4">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <StatPill label="Active" value={activeCount} tone="emerald" />
          <StatPill label="Suspended" value={suspendedCount} tone="rose" />
          <StatPill label="Admins" value={adminCount} tone="amber" />
          <StatPill label="Total" value={users.length} tone="slate" />
          <div className="ml-auto flex rounded-xl bg-white/60 p-1">
            <button onClick={() => setView("active")} data-testid="tab-active" className={tabCls(view === "active")}>Active</button>
            <button onClick={() => setView("deleted")} data-testid="tab-deleted" className={tabCls(view === "deleted")}>Recently deleted</button>
          </div>
        </div>

        {view === "active" && (<>
        <div className="mb-4 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(q)}
              placeholder="Search users… (press Enter)" className="rounded-xl bg-white/70 pl-9" data-testid="user-search-input" />
          </div>
          <Button variant="outline" className="rounded-xl bg-white/60" onClick={() => load(q)} data-testid="refresh-users"><RefreshCw className="h-4 w-4" /></Button>
        </div>

        {loading ? (
          <div className="flex justify-center p-10 text-emerald-600"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : users.length === 0 ? (
          <EmptyState label="No users found" hint="Test the connection on the Workspace Accounts page if this is unexpected." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-400">
                  <th className="px-3 py-2">User</th><th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Last login</th>
                  <th className="px-3 py-2">Org Unit</th><th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="rounded-xl border-b border-white/60 hover:bg-white/50" data-testid={`user-row-${u.primaryEmail}`}>
                    <td className="px-3 py-3">
                      <p className="font-medium text-slate-800">{u.fullName || u.primaryEmail}</p>
                      <p className="text-xs text-slate-500">{u.primaryEmail}</p>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-wrap gap-1">
                        {u.suspended ? <Badge className="bg-rose-100 text-rose-700">Suspended</Badge>
                          : u.archived ? <Badge className="bg-slate-200 text-slate-600">Archived</Badge>
                          : <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>}
                        {u.isAdmin && <Badge className="bg-amber-100 text-amber-700"><Crown className="mr-1 h-3 w-3" />Admin</Badge>}
                        {u.isEnrolledIn2Sv && <Badge className="bg-teal-100 text-teal-700"><ShieldCheck className="mr-1 h-3 w-3" />2SV</Badge>}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-500" data-testid={`lastlogin-${u.primaryEmail}`}>{timeAgo(u.lastLoginTime)}</td>
                    <td className="px-3 py-3 text-xs text-slate-500">{u.orgUnitPath}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="outline" size="sm" className="rounded-lg bg-white/60" onClick={() => setSecTarget(u)} data-testid={`security-${u.primaryEmail}`}>
                          <ShieldAlert className="mr-1 h-3.5 w-3.5" />Security
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg" data-testid={`user-menu-${u.primaryEmail}`}><MoreVertical className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-52">
                            <DropdownMenuItem onClick={() => openDetails(u)} data-testid={`details-${u.primaryEmail}`}><Info className="mr-2 h-4 w-4" />View details</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { setResetTarget(u); setResetPw(""); }} data-testid={`action-reset-${u.primaryEmail}`}><KeyRound className="mr-2 h-4 w-4" />Reset password</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => act(api.patch(`/domains/${did}/users/${u.primaryEmail}/suspension`, { suspended: !u.suspended }), u.suspended ? "Unsuspended" : "Suspended")} data-testid={`action-suspend-${u.primaryEmail}`}>
                              {u.suspended ? <Play className="mr-2 h-4 w-4" /> : <Pause className="mr-2 h-4 w-4" />}{u.suspended ? "Unsuspend" : "Suspend"}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => act(api.patch(`/domains/${did}/users/${u.primaryEmail}/archive`, { archived: !u.archived }), u.archived ? "Unarchived" : "Archived")}>
                              {u.archived ? <ArchiveRestore className="mr-2 h-4 w-4" /> : <Archive className="mr-2 h-4 w-4" />}{u.archived ? "Unarchive" : "Archive"}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { setRenameTarget(u); setRenameForm({ given_name: u.givenName || "", family_name: u.familyName || "" }); }}><Pencil className="mr-2 h-4 w-4" />Rename</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { setMoveTarget(u); setOu(u.orgUnitPath || "/"); }}><FolderInput className="mr-2 h-4 w-4" />Move Org Unit</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-rose-600" onClick={() => { if (window.confirm(`Delete ${u.primaryEmail}? This is permanent.`)) act(api.delete(`/domains/${did}/users/${u.primaryEmail}`), "User deleted"); }} data-testid={`action-delete-${u.primaryEmail}`}>
                              <Trash2 className="mr-2 h-4 w-4" />Delete user
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </>)}

        {view === "deleted" && (
          loadingDeleted ? (<div className="flex justify-center p-10 text-emerald-600"><Loader2 className="h-6 w-6 animate-spin" /></div>)
          : deleted.length === 0 ? <EmptyState label="No recently deleted users" hint="Google retains deleted users for ~20 days — restorable here." />
          : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400">
                <th className="px-3 py-2">User</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Last login</th><th className="px-3 py-2">Org Unit</th><th className="px-3 py-2 text-right">Actions</th></tr></thead>
              <tbody>
                {deleted.map((u) => (
                  <tr key={u.id} className="border-b border-white/60 hover:bg-white/50" data-testid={`deleted-row-${u.primaryEmail}`}>
                    <td className="px-3 py-3"><p className="font-medium text-slate-800">{u.fullName || u.primaryEmail}</p><p className="text-xs text-slate-500">{u.primaryEmail}</p></td>
                    <td className="px-3 py-3"><Badge className="bg-slate-200 text-slate-600">Deleted</Badge></td>
                    <td className="px-3 py-3 text-xs text-slate-500">{timeAgo(u.lastLoginTime)}</td>
                    <td className="px-3 py-3 text-xs text-slate-500">{u.orgUnitPath}</td>
                    <td className="px-3 py-3 text-right"><Button size="sm" variant="outline" className="rounded-lg bg-white/60" onClick={() => restore(u)} data-testid={`restore-${u.primaryEmail}`}><ArchiveRestore className="mr-1 h-3.5 w-3.5" />Restore</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )
        )}
      </div>

      {/* Reset password */}
      <Dialog open={!!resetTarget} onOpenChange={(o) => !o && setResetTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Reset password</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">{resetTarget?.primaryEmail} — user must change at next login.</p>
          <Input value={resetPw} onChange={(e) => setResetPw(e.target.value)} placeholder="New password (min 8)" data-testid="reset-password-input" />
          <DialogFooter>
            <Button className="glossy-emerald relative w-full rounded-xl" data-testid="submit-reset-password"
              onClick={() => { act(api.post(`/domains/${did}/users/${resetTarget.primaryEmail}/password`, { password: resetPw }), "Password reset"); setResetTarget(null); }}>
              <span className="spec-shine" />Reset password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename */}
      <Dialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Rename user</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Input value={renameForm.given_name} onChange={(e) => setRenameForm({ ...renameForm, given_name: e.target.value })} placeholder="First name" />
            <Input value={renameForm.family_name} onChange={(e) => setRenameForm({ ...renameForm, family_name: e.target.value })} placeholder="Last name" />
          </div>
          <DialogFooter><Button className="glossy-emerald relative w-full rounded-xl"
            onClick={() => { act(api.patch(`/domains/${did}/users/${renameTarget.primaryEmail}/rename`, renameForm), "Renamed"); setRenameTarget(null); }}><span className="spec-shine" />Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move OU */}
      <Dialog open={!!moveTarget} onOpenChange={(o) => !o && setMoveTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Move to Org Unit</DialogTitle></DialogHeader>
          <Input value={ou} onChange={(e) => setOu(e.target.value)} placeholder="/Sales" data-testid="move-ou-input" />
          <DialogFooter><Button className="glossy-emerald relative w-full rounded-xl"
            onClick={() => { act(api.patch(`/domains/${did}/users/${moveTarget.primaryEmail}/org-unit`, { org_unit_path: ou }), "Moved"); setMoveTarget(null); }}><span className="spec-shine" />Move</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Single-create credentials */}
      <Dialog open={!!createdCreds} onOpenChange={(o) => !o && setCreatedCreds(null)}>
        <DialogContent className="sm:max-w-md" data-testid="credentials-dialog">
          <DialogHeader><DialogTitle className="font-heading">Account created ✅</DialogTitle></DialogHeader>
          {createdCreds && (<>
            <div className="rounded-2xl bg-white/70 p-3 text-sm">
              <p className="text-slate-700"><span className="text-slate-400">Username:</span> {createdCreds.email}</p>
              <p className="text-slate-700"><span className="text-slate-400">Password:</span> <span className="font-mono">{createdCreds.password}</span></p>
            </div>
            <div className="whitespace-pre-wrap rounded-2xl bg-emerald-50 p-3 text-sm text-emerald-900" data-testid="cred-message">{credMsg(createdCreds)}</div>
            <DialogFooter>
              <Button onClick={() => copyText(credMsg(createdCreds))} className="glossy-emerald relative w-full rounded-xl" data-testid="copy-cred-message"><span className="spec-shine" /><MessageSquare className="mr-1 h-4 w-4" />Copy message for WhatsApp</Button>
            </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>

      {/* Bulk actions */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="bulk-dialog">
          <DialogHeader><DialogTitle className="font-heading">Bulk actions · {selected.domain}</DialogTitle></DialogHeader>
          <Tabs value={bulkTab} onValueChange={(v) => { setBulkTab(v); setBulkResults(null); setBulkText(""); }}>
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="create" data-testid="bulk-tab-create">Create</TabsTrigger>
              <TabsTrigger value="suspend" data-testid="bulk-tab-suspend">Suspend</TabsTrigger>
              <TabsTrigger value="unsuspend" data-testid="bulk-tab-unsuspend">Unsuspend</TabsTrigger>
              <TabsTrigger value="delete" data-testid="bulk-tab-delete">Delete</TabsTrigger>
              <TabsTrigger value="undelete" data-testid="bulk-tab-undelete">Restore</TabsTrigger>
            </TabsList>
            <TabsContent value="create" className="space-y-2">
              <p className="text-xs text-slate-500">One user per line: <span className="font-mono">First Name, Last Name, Email</span>. Passwords are auto-generated.</p>
              <textarea value={bulkText} onChange={(e) => setBulkText(e.target.value)} rows={6} data-testid="bulk-create-textarea"
                placeholder={`Jane, Doe, jane@${selected.domain}\nJohn, Smith, john@${selected.domain}`}
                className="w-full rounded-xl border border-slate-200 bg-white/70 p-3 font-mono text-sm" />
            </TabsContent>
            <TabsContent value="suspend" className="space-y-2">
              <p className="text-xs text-slate-500">Emails separated by commas or new lines. These users will be suspended.</p>
              <textarea value={bulkText} onChange={(e) => setBulkText(e.target.value)} rows={6} data-testid="bulk-suspend-textarea"
                placeholder={`a@${selected.domain}, b@${selected.domain}`}
                className="w-full rounded-xl border border-slate-200 bg-white/70 p-3 font-mono text-sm" />
            </TabsContent>
            <TabsContent value="delete" className="space-y-2">
              <p className="text-xs text-rose-500">⚠ Emails separated by commas or new lines. Deletion is permanent.</p>
              <textarea value={bulkText} onChange={(e) => setBulkText(e.target.value)} rows={6} data-testid="bulk-delete-textarea"
                placeholder={`a@${selected.domain}, b@${selected.domain}`}
                className="w-full rounded-xl border border-slate-200 bg-white/70 p-3 font-mono text-sm" />
            </TabsContent>
            <TabsContent value="unsuspend" className="space-y-2">
              <p className="text-xs text-slate-500">Emails separated by commas or new lines. These users will be re-activated.</p>
              <textarea value={bulkText} onChange={(e) => setBulkText(e.target.value)} rows={6} data-testid="bulk-unsuspend-textarea"
                placeholder={`a@${selected.domain}, b@${selected.domain}`}
                className="w-full rounded-xl border border-slate-200 bg-white/70 p-3 font-mono text-sm" />
            </TabsContent>
            <TabsContent value="undelete" className="space-y-2">
              <p className="text-xs text-slate-500">Emails of recently deleted users (Google's ~20-day window), separated by commas or new lines.</p>
              <textarea value={bulkText} onChange={(e) => setBulkText(e.target.value)} rows={6} data-testid="bulk-undelete-textarea"
                placeholder={`a@${selected.domain}, b@${selected.domain}`}
                className="w-full rounded-xl border border-slate-200 bg-white/70 p-3 font-mono text-sm" />
            </TabsContent>
          </Tabs>

          <Button onClick={runBulk} disabled={bulkBusy} className="glossy-emerald relative w-full rounded-xl" data-testid="run-bulk-button">
            <span className="spec-shine" />{bulkBusy ? "Working…" : bulkTab === "create" ? "Create users" : bulkTab === "suspend" ? "Suspend users" : bulkTab === "unsuspend" ? "Unsuspend users" : bulkTab === "undelete" ? "Restore users" : "Delete users"}
          </Button>

          {bulkResults && (
            <div className="space-y-2" data-testid="bulk-results">
              <p className="text-sm font-medium text-slate-700">{bulkResults.data.filter((r) => r.ok).length}/{bulkResults.data.length} succeeded</p>
              <div className="max-h-40 space-y-1 overflow-y-auto">
                {bulkResults.data.map((r) => (
                  <div key={r.email} className="flex items-center justify-between rounded-lg bg-white/60 px-3 py-1.5 text-xs">
                    <span className="text-slate-700">{r.email}</span>
                    {r.ok ? <Badge className="bg-emerald-100 text-emerald-700">OK{r.password ? ` · ${r.password}` : ""}</Badge> : <Badge className="bg-rose-100 text-rose-700">{r.error?.slice(0, 40) || "Failed"}</Badge>}
                  </div>
                ))}
              </div>
              {bulkResults.type === "create" && bulkResults.data.some((r) => r.ok) && (
                <Button onClick={() => copyText(bulkCreateMsg(bulkResults.data))} variant="outline" className="w-full rounded-xl bg-white/60" data-testid="copy-bulk-message"><MessageSquare className="mr-1 h-4 w-4" />Copy all credentials message</Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* User details */}
      <Dialog open={!!detailsTarget} onOpenChange={(o) => !o && setDetailsTarget(null)}>
        <DialogContent className="sm:max-w-md" data-testid="user-details-dialog">
          <DialogHeader><DialogTitle className="font-heading">User details</DialogTitle></DialogHeader>
          <p className="-mt-2 text-sm text-slate-500">{detailsTarget?.primaryEmail}</p>
          {detailsLoading ? (
            <div className="flex justify-center p-8 text-emerald-600"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : detailsData ? (
            <div className="space-y-4">
              <div className="rounded-2xl bg-white/70 p-4">
                <p className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-700"><HardDrive className="h-4 w-4" />Storage used</p>
                <p className="font-heading text-2xl font-bold text-slate-900" data-testid="detail-storage">
                  {formatBytes(detailsData.storage.usage)}
                  {detailsData.storage.limit ? <span className="text-sm font-normal text-slate-400"> / {formatBytes(detailsData.storage.limit)}</span> : null}
                </p>
                {detailsData.storage.limit && (
                  <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-200">
                    <div className="glossy-emerald h-full" style={{ width: `${Math.min(100, (Number(detailsData.storage.usage) / Number(detailsData.storage.limit)) * 100)}%` }} />
                  </div>
                )}
                <p className="mt-2 text-xs text-slate-500">In Drive: {formatBytes(detailsData.storage.usageInDrive)} · Trash: {formatBytes(detailsData.storage.usageInDriveTrash)}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-2xl bg-white/70 p-4" data-testid="detail-owned">
                  <FileText className="mb-2 h-5 w-5 text-emerald-600" />
                  <p className="font-heading text-2xl font-bold text-slate-900">{detailsData.owned_files}{detailsData.owned_capped ? "+" : ""}</p>
                  <p className="text-xs text-slate-500">Files owned</p>
                </div>
                <div className="rounded-2xl bg-white/70 p-4" data-testid="detail-shared">
                  <Share2 className="mb-2 h-5 w-5 text-teal-600" />
                  <p className="font-heading text-2xl font-bold text-slate-900">{detailsData.shared_with_user}{detailsData.shared_capped ? "+" : ""}</p>
                  <p className="text-xs text-slate-500">Shared with user</p>
                </div>
              </div>
            </div>
          ) : <p className="text-sm text-slate-500">No data available.</p>}
        </DialogContent>
      </Dialog>

      <SecuritySheet user={secTarget} did={did} onClose={() => setSecTarget(null)} onChanged={() => load(q)} act={act} />
    </div>
  );
}

function SecuritySheet({ user, did, onClose, onChanged, act }) {
  const [asps, setAsps] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [codes, setCodes] = useState(null);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!user) return;
    setLoading(true); setCodes(null);
    try {
      const [a, t] = await Promise.all([
        api.get(`/domains/${did}/users/${user.primaryEmail}/asps`),
        api.get(`/domains/${did}/users/${user.primaryEmail}/tokens`),
      ]);
      setAsps(a.data); setTokens(t.data);
    } catch (e) { /* surfaced individually */ } finally { setLoading(false); }
  }, [user, did]);

  useEffect(() => { reload(); }, [reload]);

  if (!user) return null;
  const ue = user.primaryEmail;
  const wrap = async (p, msg) => { await act(p, msg); reload(); onChanged(); };

  return (
    <Sheet open={!!user} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-md" data-testid="security-sheet">
        <SheetHeader>
          <SheetTitle className="font-heading">Security · {ue}</SheetTitle>
          <SheetDescription>Manage 2FA, sessions, admin rights, app passwords and connected apps.</SheetDescription>
        </SheetHeader>

        <div className="mt-5 space-y-3">
          <SecRow icon={Crown} title={user.isAdmin ? "Revoke Super Admin" : "Grant Super Admin"} tint="amber"
            testid="toggle-admin" onClick={() => wrap(api.post(`/domains/${did}/users/${ue}/make-admin`, { status: !user.isAdmin }), user.isAdmin ? "Admin revoked" : "Admin granted")} />
          <SecRow icon={ShieldOff} title="Turn OFF 2-Step Verification" tint="rose"
            testid="turn-off-2sv" onClick={() => wrap(api.post(`/domains/${did}/users/${ue}/turn-off-2sv`), "2SV turned off")} />
          <SecRow icon={LogOut} title="Force sign-out (revoke all sessions)" tint="teal"
            testid="force-signout" onClick={() => wrap(api.post(`/domains/${did}/users/${ue}/sign-out`), "User signed out everywhere")} />

          <div className="rounded-2xl bg-white/70 p-4">
            <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700"><ShieldCheck className="h-4 w-4" />Backup verification codes</p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="rounded-lg bg-white/60" data-testid="generate-backup-codes"
                onClick={async () => { try { const { data } = await api.post(`/domains/${did}/users/${ue}/backup-codes/generate`); setCodes(data.codes); toast.success("Codes generated"); } catch (e) { toast.error(apiError(e)); } }}>Generate</Button>
              <Button size="sm" variant="outline" className="rounded-lg bg-white/60" onClick={() => wrap(api.post(`/domains/${did}/users/${ue}/backup-codes/invalidate`), "Backup codes invalidated")}>Invalidate</Button>
            </div>
            {codes && <div className="mt-2 grid grid-cols-2 gap-1 font-mono text-xs" data-testid="backup-codes-list">{codes.map((c) => <span key={c} className="rounded bg-emerald-50 px-2 py-1 text-emerald-800">{c}</span>)}</div>}
          </div>

          <div className="rounded-2xl bg-white/70 p-4">
            <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700"><Smartphone className="h-4 w-4" />App-specific passwords</p>
            {loading ? <Loader2 className="h-4 w-4 animate-spin text-emerald-600" /> : asps.length === 0 ? <p className="text-xs text-slate-400">None</p> :
              asps.map((a) => (
                <div key={a.codeId} className="flex items-center justify-between border-b border-white/60 py-1.5 text-sm">
                  <span className="text-slate-700">{a.name}</span>
                  <Button size="sm" variant="ghost" className="h-7 text-rose-500" onClick={() => wrap(api.delete(`/domains/${did}/users/${ue}/asps/${a.codeId}`), "App password revoked")}>Revoke</Button>
                </div>
              ))}
          </div>

          <div className="rounded-2xl bg-white/70 p-4">
            <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700"><AppWindow className="h-4 w-4" />Connected apps (OAuth)</p>
            {loading ? <Loader2 className="h-4 w-4 animate-spin text-emerald-600" /> : tokens.length === 0 ? <p className="text-xs text-slate-400">None</p> :
              tokens.map((t) => (
                <div key={t.clientId} className="flex items-center justify-between border-b border-white/60 py-1.5 text-sm">
                  <span className="text-slate-700">{t.displayText}</span>
                  <Button size="sm" variant="ghost" className="h-7 text-rose-500" data-testid={`revoke-oauth-${t.clientId}`} onClick={() => wrap(api.delete(`/domains/${did}/users/${ue}/tokens/${t.clientId}`), "App access revoked")}>Revoke</Button>
                </div>
              ))}
          </div>

          <div className="rounded-2xl bg-amber-50 p-3 text-xs text-amber-700">
            "Disable login challenge" is a Google console-only control (no API). Use "Force sign-out" + "Turn off 2SV" as the equivalent, or open the Admin console.
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function StatPill({ label, value, tone }) {
  const tones = { emerald: "bg-emerald-100 text-emerald-700", rose: "bg-rose-100 text-rose-700", amber: "bg-amber-100 text-amber-700", slate: "bg-slate-100 text-slate-600" };
  return (
    <div className={`flex items-center gap-1.5 rounded-xl px-3 py-1.5 ${tones[tone]}`} data-testid={`stat-${label.toLowerCase()}`}>
      <span className="text-base font-bold">{value}</span><span className="text-xs font-medium opacity-80">{label}</span>
    </div>
  );
}
const tabCls = (active) => `rounded-lg px-3 py-1.5 text-sm font-medium transition ${active ? "bg-white text-emerald-700 shadow-sm" : "text-slate-500"}`;

function SecRow({ icon: Icon, title, onClick, tint, testid }) {
  const tints = { amber: "text-amber-600", rose: "text-rose-600", teal: "text-teal-600" };
  return (
    <button onClick={onClick} data-testid={testid}
      className="flex w-full items-center justify-between rounded-2xl bg-white/70 p-4 text-left transition hover:bg-white">
      <span className="flex items-center gap-3 text-sm font-medium text-slate-700"><Icon className={`h-5 w-5 ${tints[tint]}`} />{title}</span>
      <span className="text-xs text-emerald-600">Run →</span>
    </button>
  );
}
